import { round2 } from '@/lib/format'

export type PriceTier = 'retail' | 'wholesale'
export type DiscountType = 'percent' | 'fixed_price' | 'amount_off'
export type OfferScope = 'all' | 'categories' | 'products'
export type OfferTier = 'all' | PriceTier

/** The parts of an offer the pricing engine needs. Plain data, safe to send to the browser. */
export interface OfferRule {
    id: string
    discountType: DiscountType
    discountValue: number
    appliesTo: OfferScope
    priceTier: OfferTier
    minQty: number
    startsAt: string | null
    endsAt: string | null
    productIds: Array<string>
    categoryIds: Array<string>
}

export interface PricingTarget {
    productId: string
    categoryId: string | null
}

export interface BasePrices {
    retail: number
    wholesale: number | null
    wholesaleMinQty: number
}

const parseTime = (value: string | null): number | null => {
    if (!value) return null

    const time = Date.parse(value)

    return Number.isNaN(time) ? null : time
}

export const isOfferLive = (offer: Pick<OfferRule, 'startsAt' | 'endsAt'>, now: number): boolean => {
    const start = parseTime(offer.startsAt)
    const end = parseTime(offer.endsAt)

    if (start !== null && start > now) return false
    if (end !== null && end <= now) return false

    return true
}

export const isOfferUpcoming = (offer: Pick<OfferRule, 'startsAt'>, now: number): boolean => {
    const start = parseTime(offer.startsAt)

    return start !== null && start > now
}

export const offerAppliesTo = (offer: OfferRule, target: PricingTarget): boolean => {
    if (offer.appliesTo === 'all') return true
    if (offer.appliesTo === 'products') return offer.productIds.includes(target.productId)
    if (offer.appliesTo === 'categories') return target.categoryId !== null && offer.categoryIds.includes(target.categoryId)

    return false
}

export const applyDiscount = (offer: Pick<OfferRule, 'discountType' | 'discountValue'>, unit: number): number => {
    const value = Math.max(0, offer.discountValue)

    if (offer.discountType === 'percent') return round2(unit * (1 - Math.min(value, 100) / 100))
    if (offer.discountType === 'fixed_price') return value < unit ? round2(value) : unit

    return round2(Math.max(0, unit - value))
}

export const isWholesaleAvailable = (retail: number, wholesale: number | null): wholesale is number =>
    wholesale !== null && wholesale > 0 && wholesale < retail

interface OfferPick {
    price: number
    offer: OfferRule | null
}

export const bestOfferPrice = (unit: number, tier: PriceTier, qty: number, target: PricingTarget, offers: Array<OfferRule>, now: number): OfferPick => {
    let price = unit
    let chosen: OfferRule | null = null

    for (const offer of offers) {
        if (!isOfferLive(offer, now)) continue
        if (!offerAppliesTo(offer, target)) continue
        if (offer.priceTier !== 'all' && offer.priceTier !== tier) continue
        if (qty < offer.minQty) continue

        const candidate = applyDiscount(offer, unit)

        if (candidate < price - 0.0001) {
            price = candidate
            chosen = offer
        }
    }

    return { price, offer: chosen }
}

export interface LinePricing {
    tier: PriceTier
    retailUnit: number
    wholesaleUnit: number | null
    wholesaleAvailable: boolean
    tierUnit: number
    finalUnit: number
    offer: OfferRule | null
    subtotal: number
    discount: number
    total: number
    wholesaleSavings: number
    qtyToWholesale: number
}

/**
 * Prices one cart line. The wholesale price applies once the quantity reaches
 * the wholesale minimum. Offers are evaluated on both tiers and the customer
 * always gets the lowest resulting unit price; offers never stack.
 */
export const priceLine = (base: BasePrices, qty: number, target: PricingTarget, offers: Array<OfferRule>, now: number): LinePricing => {
    const quantity = Math.max(1, Math.floor(qty))
    const retailUnit = round2(base.retail)
    const wholesaleAvailable = isWholesaleAvailable(retailUnit, base.wholesale)
    const wholesaleUnit = wholesaleAvailable ? round2(base.wholesale as number) : null
    const qualifiesForWholesale = wholesaleAvailable && quantity >= base.wholesaleMinQty

    const retailPick = bestOfferPrice(retailUnit, 'retail', quantity, target, offers, now)
    let tier: PriceTier = 'retail'
    let tierUnit = retailUnit
    let pick = retailPick

    if (qualifiesForWholesale && wholesaleUnit !== null) {
        const wholesalePick = bestOfferPrice(wholesaleUnit, 'wholesale', quantity, target, offers, now)

        if (wholesalePick.price <= retailPick.price) {
            tier = 'wholesale'
            tierUnit = wholesaleUnit
            pick = wholesalePick
        }
    }

    const finalUnit = pick.price
    const subtotal = round2(tierUnit * quantity)
    const total = round2(finalUnit * quantity)

    return {
        tier,
        retailUnit,
        wholesaleUnit,
        wholesaleAvailable,
        tierUnit,
        finalUnit,
        offer: pick.offer,
        subtotal,
        discount: round2(subtotal - total),
        total,
        wholesaleSavings: tier === 'wholesale' ? round2((retailUnit - tierUnit) * quantity) : 0,
        qtyToWholesale: wholesaleAvailable && !qualifiesForWholesale ? Math.max(0, base.wholesaleMinQty - quantity) : 0
    }
}

export interface DisplayPricing {
    retail: number
    retailFinal: number
    retailOfferId: string | null
    wholesale: number | null
    wholesaleFinal: number | null
    wholesaleOfferId: string | null
    wholesaleMinQty: number
    wholesaleSavingPct: number
    bulkOffer: OfferRule | null
}

/** Prices shown on cards and at the top of a product page, before a quantity is chosen. */
export const displayPricing = (base: BasePrices, target: PricingTarget, offers: Array<OfferRule>, now: number): DisplayPricing => {
    const retail = round2(base.retail)
    const retailPick = bestOfferPrice(retail, 'retail', 1, target, offers, now)
    const wholesaleAvailable = isWholesaleAvailable(retail, base.wholesale)
    const wholesale = wholesaleAvailable ? round2(base.wholesale as number) : null
    const wholesalePick = wholesale !== null ? bestOfferPrice(wholesale, 'wholesale', base.wholesaleMinQty, target, offers, now) : null
    const threshold = wholesale !== null ? base.wholesaleMinQty : 1

    const bulkOffer = offers.find(offer =>
        isOfferLive(offer, now)
        && offerAppliesTo(offer, target)
        && offer.minQty > threshold
    ) ?? null

    const wholesaleFinal = wholesalePick ? wholesalePick.price : null
    const savingPct = wholesaleFinal !== null && retailPick.price > 0
        ? Math.max(0, Math.round(((retailPick.price - wholesaleFinal) / retailPick.price) * 100))
        : 0

    return {
        retail,
        retailFinal: retailPick.price,
        retailOfferId: retailPick.offer?.id ?? null,
        wholesale,
        wholesaleFinal,
        wholesaleOfferId: wholesalePick?.offer?.id ?? null,
        wholesaleMinQty: base.wholesaleMinQty,
        wholesaleSavingPct: savingPct,
        bulkOffer
    }
}

export interface TotalsInput {
    lines: Array<Pick<LinePricing, 'subtotal' | 'discount' | 'total' | 'wholesaleSavings'>>
    shippingFee: number | null
    freeShippingEnabled: boolean
    freeShippingThreshold: number
}

export interface CartTotals {
    subtotal: number
    discount: number
    merchandise: number
    shipping: number | null
    freeShipping: boolean
    total: number
    wholesaleSavings: number
    amountToFreeShipping: number
}

export const computeTotals = ({ lines, shippingFee, freeShippingEnabled, freeShippingThreshold }: TotalsInput): CartTotals => {
    const subtotal = round2(lines.reduce((sum, line) => sum + line.subtotal, 0))
    const discount = round2(lines.reduce((sum, line) => sum + line.discount, 0))
    const merchandise = round2(subtotal - discount)
    const wholesaleSavings = round2(lines.reduce((sum, line) => sum + line.wholesaleSavings, 0))
    const thresholdActive = freeShippingEnabled && freeShippingThreshold > 0
    const freeShipping = thresholdActive && merchandise >= freeShippingThreshold && lines.length > 0
    const shipping = shippingFee === null ? null : freeShipping ? 0 : round2(shippingFee)

    return {
        subtotal,
        discount,
        merchandise,
        shipping,
        freeShipping,
        total: round2(merchandise + (shipping ?? 0)),
        wholesaleSavings,
        amountToFreeShipping: thresholdActive && !freeShipping ? round2(Math.max(0, freeShippingThreshold - merchandise)) : 0
    }
}
